"use client";
import React, { useState, useEffect, useRef } from "react";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { useModalQuickviewContext } from "../../context/ModalQuickviewContext";
import { useCart, notifyCartStorageChanged } from "../../context/CartContext";
import { useModalCartContext } from "../../context/ModalCartContext";
import { useWishlist } from "../../context/WishlistContext";
import { useModalWishlistContext } from "../../context/ModalWishlistContext";
import { useCompare } from "../../context/CompareContext";
import { useModalCompareContext } from "../../context/ModalCompareContext";
import { useToast } from "../../context/ToastContext";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from 'swiper';
import { Navigation, Thumbs, Scrollbar } from "swiper/modules";
import "swiper/css/bundle";
import { getApiUrl } from "../../config/api";
import { makeCartVariantKey } from "../../utils/cartVariantKey";
import { displayNomUpper } from "../../utils/displayNom";
import { PriceDisplay } from "../Other/PriceDisplay";

const formatDate = (date: any) => {
  const options = { day: "numeric", month: "long" }
  return date.toLocaleDateString("fr-FR", options)
};

function isLightColor(rgb: string | undefined, colorName?: string): boolean {
  if (/blanc|white/i.test(colorName || '')) return true;
  if (!rgb) return false;
  const hex = rgb.replace(/^#/, '').replace(/^([0-9a-f])([0-9a-f])([0-9a-f])$/i, (_, r, g, b) => r + r + g + g + b + b);
  if (hex.length !== 6) return false;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 220;
}

// Function to generate image URLs for a product
const generateProductImages = (productId: string | number): string[] => {
  const images: string[] = [];
  const baseUrl = `/images/`;
  
  // Add main image (e.g., "77.jpg")
  images.push(`${baseUrl}${productId}.jpg`);
  
  // Add numbered images (e.g., "77-1.jpg", "77-2.jpg", etc.)
  for (let i = 1; i <= 10; i++) {
    images.push(`${baseUrl}${productId}-${i}.jpg`);
  }
  
  return images;
};

// Function to check if an image exists
const checkImageExists = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      console.log(`Image found: ${url}`);
      resolve(true);
    };
    img.onerror = () => {
      console.log(`Image not found: ${url}`);
      resolve(false);
    };
    img.src = url;
  });
};

// Function to get available images for a product
const getAvailableImages = async (productId: string | number): Promise<string[]> => {
  const allImages = generateProductImages(productId);
  const availableImages: string[] = [];
  
  console.log(`Checking for images:`, allImages);
  
  for (const imageUrl of allImages) {
    const exists = await checkImageExists(imageUrl);
    if (exists) {
      availableImages.push(imageUrl);
    }
  }
  
  // If no images found, return default image
  if (availableImages.length === 0) {
    console.log(`No images found for product ${productId}, using default image`);
    availableImages.push("/default.png");
  }
  
  return availableImages;
};

const ModalQuickview = () => {
  const swiperRef: any = useRef();
  const { selectedProduct, closeQuickview } = useModalQuickviewContext();
  const [activeColor, setActiveColor] = useState<string>("");
  const [activeSize, setActiveSize] = useState<string>("");
  const { addToCart, updateCart, cartState } = useCart();
  const { openModalCart } = useModalCartContext();
  const { addToWishlist, removeFromWishlist, wishlistState } = useWishlist();
  const { openModalWishlist } = useModalWishlistContext();
  const { addToCompare, removeFromCompare, compareState } = useCompare();
  const { openModalCompare } = useModalCompareContext();
  const { showToast } = useToast();
  const [colors, setColors] = useState<{ nom: string,rgb:string,size:number,pointure:number,code:number,imageid?:number }[]>([]);
  const [sizes, setSizes] = useState<{ nom: string,code:number }[]>([]);
  const [size, setSize] = useState<number>(0);
  const [color, setColor] = useState<number>(0);
  const [Qtn, setQtn] = useState<number>(1);
  const [pointures, setpointures] = useState<{ nom: string,code:number }[]>([]);
  const [pointure, setpointure] = useState<number>(0);
  const [activepointure, setactivepointure] = useState<string>("");
  const [colorImages, setColorImages] = useState<{[key: number]: string, selectedColorImage?: string}>({});
  const [hasCharacteristics, setHasCharacteristics] = useState<boolean>(true);
  const [productImages, setProductImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);

  const handleIncreaseQuantity = () => {
    setQtn((prv) => prv + 1);
  };

  const handleDecreaseQuantity = () => {
    if (Qtn > 1) {
      setQtn((prv) => prv - 1);
    }
  };

  useEffect(() => {
    const loadImages = async () => {
      if (selectedProduct?.id) {
        try {
          const images = await getAvailableImages(selectedProduct.id);
          setProductImages(images);
          console.log('Loaded product images:', images);
        } catch (error) {
          console.error('Error loading product images:', error);
          setProductImages(["/default.png"]);
        }
      }
    };

    const fetchCaracteristiques = async () => {
      if (selectedProduct?.id) {
        try {
          console.log('Fetching characteristics for product:', selectedProduct.id);
          const response = await fetch(`${getApiUrl('CARACTERISTIQUE_ALL')}?id=${selectedProduct.id}`);
          const data = await response.json(); 
          
          console.log('API Response:', data);
          console.log('Colors from API:', data.colors);
          console.log('Response status:', response.status);
          console.log('Response ok:', response.ok);
          
          // Check if product has characteristics
          console.log('API Response colors:', data.colors);
          if (!data.colors || data.colors.length === 0) {
            console.log('Product has no characteristics, using product images as colors');
            setHasCharacteristics(false);
            setColors([]);
            setSizes([]);
            setpointures([]);
            return;
          }
          
          // Product has characteristics
          console.log('Product has characteristics, setting hasCharacteristics to true');
          setHasCharacteristics(true);
          
          // Remove duplicate colors based on name
          const uniqueColors = data.colors?.filter((color: any, index: number, self: any[]) => 
            index === self.findIndex((c: any) => c.nom === color.nom)
          ) || [];
          
          console.log('Unique colors after filtering:', uniqueColors);
          setColors(uniqueColors);
          console.log('Setting colors state to:', uniqueColors); 
          // Don't load all sizes initially - they will be loaded when color is selected
          setSizes([]); 
          setpointures([]);
        } catch (error) {
          console.error('Error fetching product characteristics:', error);
          // If error occurs, assume product has no characteristics
          console.log('Error occurred, setting hasCharacteristics to false');
          setHasCharacteristics(false);
          setColors([]);
          setSizes([]);
          setpointures([]);
        }
      }
    };

    loadImages();
    fetchCaracteristiques();
  }, [selectedProduct?.id]);

  // Set color image paths when colors are loaded
  useEffect(() => {
    colors.forEach(color => {
      if (color.imageid && !colorImages[color.imageid]) {
        fetchColorImage(color.imageid);
      }
    });
  }, [colors]);

  // Debug hasCharacteristics state changes
  useEffect(() => {
    console.log('hasCharacteristics changed to:', hasCharacteristics);
  }, [hasCharacteristics]);

  const initialColorAndSizeSet = useRef(false);
  useEffect(() => {
    initialColorAndSizeSet.current = false;
  }, [selectedProduct?.id]);
  useEffect(() => {
    if (!selectedProduct?.id || colors.length === 0 || initialColorAndSizeSet.current) return;
    initialColorAndSizeSet.current = true;
    const first = colors[0];
    setActiveColor(first.nom);
    setColor(first.code);
    fetchFilteredSizes(selectedProduct.id, first.code);
    if (first?.imageid) {
      const colorImagePath = `/images/${first.imageid}`;
      setProductImages(prev => {
        const otherImages = prev.filter(img => img !== colorImagePath);
        return [colorImagePath, ...otherImages];
      });
      setSelectedImageIndex(0);
      if (swiperRef.current?.swiper) {
        swiperRef.current.swiper.slideTo(0);
      }
    }
  }, [colors, selectedProduct?.id]);

  const handleAddToCartProd = () => {
    // For products without characteristics, skip validation and use product images
    if (!hasCharacteristics) {
      const cartItemsJSON = localStorage.getItem("cartItems");
      const cartItems: any[] = cartItemsJSON ? JSON.parse(cartItemsJSON) : [];

      // Use the currently selected product image as the color image
      const selectedImagePath = productImages[selectedImageIndex];
      
      // Extract just the filename from the path (e.g., "/images/59-2.jpg" -> "59-2.jpg")
      const selectedImageFilename = selectedImagePath ? selectedImagePath.split('/').pop() : null;

      console.log("Adding product without characteristics to cart (Modal):", {
        productId: selectedProduct?.id,
        selectedImagePath,
        selectedImageFilename,
        colorImageId: selectedImageFilename || null
      });

      const newCartItem: any = {
        id: selectedProduct?.id, 
        quantity: Qtn, 
        colorCode: 0, // No color code for products without characteristics
        sizeCode: 0, // No size code for products without characteristics
        pointureCode: 0, // No pointure code for products without characteristics
        color: "Standard", // Default color name
        size: "",
        pointure: "",
        colorImageId: selectedImageFilename || null, // Use just the filename
        livraison: selectedProduct?.slug
      };

      if (!newCartItem.id) {
        console.error("Product ID is not defined.");
        return; 
      }

      const newVariantKey = makeCartVariantKey({
        productId: newCartItem.id,
        colorCode: newCartItem.colorCode,
        sizeCode: newCartItem.sizeCode,
        pointureCode: newCartItem.pointureCode,
      });

      const existingItemIndex = cartItems.findIndex((item) => {
        const itemVariantKey = makeCartVariantKey({
          productId: item.id,
          colorCode: item.colorCode,
          sizeCode: item.sizeCode,
          pointureCode: item.pointureCode,
        });
        return itemVariantKey === newVariantKey;
      });

      if (existingItemIndex >= 0) {
        cartItems[existingItemIndex].quantity += newCartItem.quantity;
      } else {
        cartItems.push(newCartItem);
      }

      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      notifyCartStorageChanged();
      openModalCart();
      closeQuickview();
      return;
    }

    // For products with characteristics, use existing validation logic
    // Check if color is required and selected
    if (colors && colors.length > 0 && !activeColor) {
      showToast("Veuillez sélectionner une couleur", "warning");
      return;
    }
    
    // Check if size is required and selected
    if (sizes && sizes.length > 0 && !activeSize) {
      showToast("Veuillez sélectionner une taille", "warning");
      return;
    }
    
    // Check if pointure is required and selected
    if (pointures && pointures.length > 0 && !activepointure) {
      showToast("Veuillez sélectionner une pointure", "warning");
      return;
    }

    const cartItemsJSON = localStorage.getItem("cartItems");
    const cartItems: any[] = cartItemsJSON ? JSON.parse(cartItemsJSON) : [];

    // Get the selected color's imageid
    const selectedColor = colors.find(c => c.nom === activeColor);
    const colorImageId = selectedColor?.imageid;

    console.log("Adding product with characteristics to cart (Modal):", {
      productId: selectedProduct?.id,
      selectedColor: selectedColor?.nom,
      colorImageId,
      activeColor,
      activeSize,
      activepointure
    });

    const newCartItem: any = {
      id: selectedProduct?.id, 
      quantity: Qtn, 
      colorCode: color, 
      sizeCode: size,
      pointureCode: pointure,
      color: activeColor,
      size: activeSize,
      pointure:activepointure,
      colorImageId: colorImageId,
      livraison:selectedProduct?.slug
    };

    if (!newCartItem.id) {
      console.error("Product ID is not defined.");
      return; 
    }

    const newVariantKey = makeCartVariantKey({
      productId: newCartItem.id,
      colorCode: newCartItem.colorCode,
      sizeCode: newCartItem.sizeCode,
      pointureCode: newCartItem.pointureCode,
    });

    const existingItemIndex = cartItems.findIndex((item) => {
      const itemVariantKey = makeCartVariantKey({
        productId: item.id,
        colorCode: item.colorCode,
        sizeCode: item.sizeCode,
        pointureCode: item.pointureCode,
      });
      return itemVariantKey === newVariantKey;
    });

    if (existingItemIndex >= 0) {
      cartItems[existingItemIndex].quantity += newCartItem.quantity;
    } else {
      cartItems.push(newCartItem);
    }

    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    notifyCartStorageChanged();

    openModalCart();
    closeQuickview();
  };

  const handleAddToWishlist = () => {
    if (selectedProduct) {
      if (
        wishlistState.wishlistArray.some(
          (item) => item.id === selectedProduct.id
        )
      ) {
        removeFromWishlist(selectedProduct.id);
      } else {
        addToWishlist(selectedProduct);
      }
    }
    openModalWishlist();
  };

  const handleAddToCompare = () => {
    if (selectedProduct) {
      if (compareState.compareArray.length < 3) {
        if (
          compareState.compareArray.some(
            (item) => item.id === selectedProduct.id
          )
        ) {
          removeFromCompare(selectedProduct.id);
        } else {
          addToCompare(selectedProduct);
        }
      } else {
        alert("Compare up to 3 products");
      }
    }
    openModalCompare();
  };

  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() + 4);
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + 8);

  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);

  const funcSize = (s:string,c:number) => {
    setActiveSize(s);
    setSize(c);
  };

  const funcpointure = (s:string,c:number) => {
    setactivepointure(s);
    setpointure(c);
  };

  const funcColor = (s:string,c:number) => {
    setActiveColor(s);
    setColor(c);
    // Reset size and pointure when color changes
    setActiveSize("");
    setSize(0);
    setactivepointure("");
    setpointure(0);
    
    // Fetch filtered sizes and pointures for this color
    if (selectedProduct?.id) {
      fetchFilteredSizes(selectedProduct.id, c);
    }
    
    // Update the main product image to show the selected color's image
    const selectedColor = colors.find(color => color.nom === s);
    if (selectedColor?.imageid) {
      const colorImagePath = `/images/${selectedColor.imageid}`;
      
      // Find the index of the color image in the current productImages array
      const colorImageIndex = productImages.findIndex(img => img === colorImagePath);
      
      if (colorImageIndex !== -1) {
        // If the color image exists in the array, set it as selected
        setSelectedImageIndex(colorImageIndex);
        
        // Update the Swiper to show the color image
        if (swiperRef.current && swiperRef.current.swiper) {
          swiperRef.current.swiper.slideTo(colorImageIndex);
        }
      } else {
        // If the color image doesn't exist, add it to the beginning
        setProductImages(prev => {
          const otherImages = prev.filter(img => img !== colorImagePath);
          return [colorImagePath, ...otherImages];
        });
        setSelectedImageIndex(0);
        
        // Update the Swiper to show the first slide (the color image)
        if (swiperRef.current && swiperRef.current.swiper) {
          swiperRef.current.swiper.slideTo(0);
        }
      }
    }
  };

  const fetchFilteredSizes = async (productId: string | number, colorCode: number) => {
    try {
      const response = await fetch(`${getApiUrl('CARACTERISTIQUE')}?code_art=${productId}&color=${colorCode}`);
      const data = await response.json();
      
      if (data.success) {
        const sizeList = data.sizes || [];
        const pointureList = data.pointures || [];
        setSizes(sizeList);
        setpointures(pointureList);
        if (sizeList.length > 0) {
          setActiveSize(sizeList[0].nom);
          setSize(sizeList[0].code);
        }
        if (pointureList.length > 0) {
          setactivepointure(pointureList[0].nom);
          setpointure(pointureList[0].code);
        }
        console.log('Quick view - Filtered sizes for color', colorCode, ':', data.sizes);
        console.log('Quick view - Filtered pointures for color', colorCode, ':', data.pointures);
      } else {
        console.error('Error fetching filtered sizes in quick view:', data.error);
      }
    } catch (error) {
      console.error('Error fetching filtered sizes in quick view:', error);
    }
  };

  const fetchColorImage = async (imageid: number) => {
    if (!imageid || colorImages[imageid]) return; // Already loaded or no imageid
    
    // Use direct path instead of API call
    const imagePath = `/images/${imageid}`;
    setColorImages(prev => ({
      ...prev,
      [imageid]: imagePath
    }));
  };


  return (
    <>
      <div className={`modal-quickview-block`} onClick={closeQuickview}>
        <div
          className={`modal-quickview-main py-6 ${
            selectedProduct !== null ? "open" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="flex h-full max-md:flex-col-reverse gap-y-6">
            <div className="left lg:w-[388px] md:w-[300px] flex-shrink-0 px-6">
              {/* Main Image Gallery */}
              <div className="main-image-gallery mb-4">
                <Swiper
                  ref={swiperRef}
                  spaceBetween={0}
                  slidesPerView={1}
                  navigation={false}
                  thumbs={{ swiper: thumbsSwiper && !(thumbsSwiper as any).destroyed ? thumbsSwiper : null }}
                  modules={[Navigation, Thumbs]}
                  className="main-swiper"
                  onSlideChange={(swiper: SwiperClass) => {
                    setSelectedImageIndex(swiper.activeIndex);
                    // Check if the selected image corresponds to a color and select it
                    const selectedImagePath = productImages[swiper.activeIndex];
                    const matchingColor = colors.find(color => 
                      color.imageid && `/images/${color.imageid}` === selectedImagePath
                    );
                    if (matchingColor) {
                      funcColor(matchingColor.nom, matchingColor.code);
                    }
                  }}
                >
                  {productImages.map((image, index) => (
                    <SwiperSlide key={index}>
                      <div className="bg-img w-full aspect-[3/4] rounded-[20px] overflow-hidden cursor-pointer">
                        <img
                          src={image}
                          alt={`${selectedProduct?.name} - Image ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = "/default.png";
                          }}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              {/* Thumbnail Gallery */}
              {productImages.length > 1 && (
                <div className="thumbnail-gallery">
                                     <Swiper
                     onSwiper={(swiper: SwiperClass) => setThumbsSwiper(swiper)}
                    spaceBetween={8}
                    slidesPerView={4}
                    freeMode={true}
                    watchSlidesProgress={true}
                    modules={[Thumbs]}
                    className="thumbnail-swiper"
                  >
                    {productImages.map((image, index) => (
                      <SwiperSlide key={index}>
                        <div 
                          className={`thumbnail-item w-full aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-300 ${
                            index === selectedImageIndex 
                              ? 'border-pink shadow-md' 
                              : 'border-gray-200 hover:border-pink'
                          }`}
                          onClick={() => {
                            if (swiperRef.current && swiperRef.current.swiper) {
                              swiperRef.current.swiper.slideTo(index);
                            }
                          }}
                        >
                          <img
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = "/default.png";
                            }}
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              )}
            </div>
            <div className="right w-full px-4">
              <div className="heading pb-6 px-4 flex items-center justify-between relative">
              <div className="heading5">Aperçu rapide</div>
              <div
                  className="close-btn absolute right-0 top-0 w-6 h-6 rounded-full bg-surface flex items-center justify-center duration-300 cursor-pointer hover:bg-black hover:text-white"
                  onClick={closeQuickview}
                >
                  <Icon.X size={14} />
                </div>
              </div>
              <div className="product-infor px-4">
                <div className="flex justify-between">
                  <div>
                    <div className="caption2 text-secondary font-semibold uppercase">
                      {selectedProduct?.type}
                    </div>
                    <div className="heading4 mt-1">{selectedProduct?.name}</div>
                  </div>
                  <div
                    className={`add-wishlist-btn w-10 h-10 flex items-center justify-center border border-line cursor-pointer rounded-lg duration-300 flex-shrink-0 hover:bg-black hover:text-white ${
                      wishlistState.wishlistArray.some(
                        (item) => item.id === selectedProduct?.id
                      )
                        ? "active"
                        : ""
                    }`}
                    onClick={handleAddToWishlist}
                  >
                    {wishlistState.wishlistArray.some(
                      (item) => item.id === selectedProduct?.id
                    ) ? (
                      <>
                        <Icon.Heart
                          size={20}
                          weight="fill"
                          className="text-red"
                        />
                      </>
                    ) : (
                      <>
                        <Icon.Heart size={20} />
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-3 flex-wrap mt-5 pb-6 border-b border-line">
                  <div className="product-price heading5">
                    <PriceDisplay amount={selectedProduct?.price} size="xl" />
                  </div>
                  <div className="w-px h-4 bg-line"></div>
                  <div className="product-origin-price font-normal text-secondary2">
                    <del>
                      <PriceDisplay
                        amount={
                          selectedProduct?.price != null
                            ? Number(selectedProduct.price) * 1.1
                            : 0
                        }
                        size="md"
                      />
                    </del>
                  </div>
                  
                  <div className="desc text-secondary mt-3">
                    {selectedProduct?.description}
                  </div>
                </div>
                {/* Colors Section - Show First (only if colors exist) */}
                {(colors && colors.length > 0) && (
                  <>
                    <div className="text-title mt-5">Couleurs:</div>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      {colors.map((c, i) => (
                        <div key={i} className="flex flex-col items-center">
                          {/* Small image above color */}
                          {c.imageid && (
                            <div className="relative group">
                              <img
                                src={colorImages[c.imageid!] || `/images/${c.imageid}`}
                                alt={`${c.nom} preview`}
                                className="w-12 h-12 object-cover rounded-lg border-2 transition-all duration-300 cursor-pointer"
                                style={{
                                  borderColor: c.rgb,
                                  boxShadow: c.nom == activeColor 
                                    ? `0 0 0 2px ${c.rgb}, 0 4px 8px rgba(0, 0, 0, 0.1)` 
                                    : `0 0 0 2px ${c.rgb}`
                                }}
                                onClick={() => {
                                  // Show sizes when image is clicked
                                  funcColor(c.nom, c.code);
                                }}
                                onError={(e) => {
                                  e.currentTarget.src = "/default.png";
                                }}
                              />
                              
                              {/* Selection indicator */}
                              {c.nom == activeColor && (
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-pink rounded-full flex items-center justify-center">
                                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                                </div>
                              )}
                              
                              {/* Tooltip - Hidden on mobile for better UX */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 hidden md:block">
                                {c.nom}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Product Images as Colors Section - For products without characteristics */}
                {(!hasCharacteristics && productImages.length > 0) && (
                  <>
                    <div className="text-title mt-5">Variantes:</div>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      {productImages.map((image, i) => (
                        <div key={i} className="flex flex-col items-center">
                          <div className="relative group">
                            <img
                              src={image}
                              alt={`Variante ${i + 1}`}
                              className="w-16 h-16 object-cover rounded-lg border-2 transition-all duration-300 cursor-pointer"
                              style={{
                                borderColor: '#ff4370',
                                boxShadow: i === selectedImageIndex
                                  ? `0 0 0 2px #ff4370, 0 4px 8px rgba(255, 67, 112, 0.3)`
                                  : `0 0 0 2px #ff4370`
                              }}
                              onClick={() => {
                                setSelectedImageIndex(i);
                                // Update the Swiper to show the selected image
                                if (swiperRef.current && swiperRef.current.swiper) {
                                  swiperRef.current.swiper.slideTo(i);
                                }
                              }}
                              onError={(e) => {
                                e.currentTarget.src = "/default.png";
                              }}
                            />
                            
                            {/* Selection indicator */}
                            {i === selectedImageIndex && (
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-pink rounded-full flex items-center justify-center">
                                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                              </div>
                            )}
                            
                            {/* Tooltip - Hidden on mobile for better UX */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 hidden md:block">
                              Variante {i + 1}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Sizes Section - Show after color selection (only if sizes exist and product has characteristics) */}
                {(hasCharacteristics && activeColor && sizes && sizes.length > 0) && (
                  <>
                    <div className="text-title mt-5">Tailles:</div>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      {sizes?.map((s, i) => {
                        const selectedColor = colors.find(c => c.nom === activeColor);
                        const useDarkText = isLightColor(selectedColor?.rgb, selectedColor?.nom);
                        const textClass = useDarkText ? 'text-gray-800' : 'text-white';
                        const hoverTextClass = useDarkText ? 'hover:text-gray-800' : 'hover:text-white';
                        return (
                          <button
                            key={i}
                            onClick={() => funcSize(s.nom,s.code)}
                            className={`text-lg md:text-xl font-medium border px-3 md:px-4 py-2 md:py-2 rounded-lg transition-all duration-300 touch-manipulation min-h-[44px] ${
                              s.nom === activeSize ? textClass : hoverTextClass
                            }`}
                            style={{
                              borderColor: selectedColor?.rgb || '#e5e7eb',
                              backgroundColor: s.nom === activeSize ? (selectedColor?.rgb || '#ff4370') : 'transparent',
                              boxShadow: s.nom === activeSize 
                                ? `0 4px 8px ${selectedColor?.rgb}40` 
                                : 'none'
                            }}
                            onMouseEnter={(e) => {
                              if (s.nom !== activeSize) {
                                e.currentTarget.style.backgroundColor = selectedColor?.rgb || '#ff4370';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (s.nom !== activeSize) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {displayNomUpper(s.nom)}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Pointures Section - Show after color selection (only if pointures exist and product has characteristics) */}
                {(hasCharacteristics && activeColor && pointures && pointures.length > 0) && (
                  <>
                    <div className="text-title mt-5">Pointures:</div>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      {pointures?.map((s, i) => {
                        const selectedColor = colors.find(c => c.nom === activeColor);
                        const useDarkText = isLightColor(selectedColor?.rgb, selectedColor?.nom);
                        const textClass = useDarkText ? 'text-gray-800' : 'text-white';
                        const hoverTextClass = useDarkText ? 'hover:text-gray-800' : 'hover:text-white';
                        return (
                          <button
                            key={i}
                            onClick={() => funcpointure(s.nom,s.code)}
                            className={`text-lg md:text-xl font-medium border px-3 md:px-4 py-2 md:py-2 rounded-lg transition-all duration-300 touch-manipulation min-h-[44px] ${
                              s.nom === activepointure ? textClass : hoverTextClass
                            }`}
                            style={{
                              borderColor: selectedColor?.rgb || '#e5e7eb',
                              backgroundColor: s.nom === activepointure ? (selectedColor?.rgb || '#ff4370') : 'transparent',
                              boxShadow: s.nom === activepointure 
                                ? `0 4px 8px ${selectedColor?.rgb}40` 
                                : 'none'
                            }}
                            onMouseEnter={(e) => {
                              if (s.nom !== activepointure) {
                                e.currentTarget.style.backgroundColor = selectedColor?.rgb || '#ff4370';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (s.nom !== activepointure) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {displayNomUpper(s.nom)}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                  <div className="text-title mt-5">Quantité :</div>
                  <div className="choose-quantity flex items-center max-xl:flex-wrap lg:justify-between gap-5 mt-3">
                    <div className="quantity-block md:p-3 max-md:py-1.5 max-md:px-3 flex items-center justify-between rounded-lg border border-line sm:w-[180px] w-[120px] flex-shrink-0">
                      <Icon.Minus
                        onClick={handleDecreaseQuantity}
                        className={`${
                          Qtn == 1
                            ? "disabled"
                            : ""
                        } cursor-pointer body1 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center`}
                      />
                      <div className="body1 font-semibold">
                        {Qtn}
                      </div>
                      <Icon.Plus
                        onClick={handleIncreaseQuantity}
                        className="cursor-pointer body1 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                      />
                    </div>
                    <div
                      onClick={() => {
                        // Check if color is required and selected
                        if (colors && colors.length > 0 && !activeColor) {
                          showToast("Veuillez sélectionner une couleur", "warning");
                          return;
                        }
                        
                        // Check if size is required and selected
                        if (sizes && sizes.length > 0 && !activeSize) {
                          showToast("Veuillez sélectionner une taille", "warning");
                          return;
                        }
                        
                        // Check if pointure is required and selected
                        if (pointures && pointures.length > 0 && !activepointure) {
                          showToast("Veuillez sélectionner une pointure", "warning");
                          return;
                        }
                        
                        handleAddToCartProd();
                      }}
                      className="button-main w-full text-center transition-all duration-200 bg-pink hover:bg-green-600 hover:text-black flex items-center justify-center gap-2 min-h-[44px]"
                    >
                      Ajouter au panier
                    </div>
                  </div>
                  <div className="button-block mt-5">
                    <div 
                      className="button-main w-full text-center transition-all duration-200 py-3 bg-pink hover:bg-greenprimary hover:text-white text-lg flex items-center justify-center gap-2 rounded-lg shadow-lg attention-button relative overflow-hidden group min-h-[44px]"
                      onClick={async() => {
                        // Check if color is required and selected
                        if (colors && colors.length > 0 && !activeColor) {
                          showToast("Veuillez sélectionner une couleur", "warning");
                          return;
                        }
                        
                        // Check if size is required and selected
                        if (sizes && sizes.length > 0 && !activeSize) {
                          showToast("Veuillez sélectionner une taille", "warning");
                          return;
                        }
                        
                        // Check if pointure is required and selected
                        if (pointures && pointures.length > 0 && !activepointure) {
                          showToast("Veuillez sélectionner une pointure", "warning");
                          return;
                        }
                        
                        await handleAddToCartProd();
                        window.location.href = `/cart`;
                      }}
                    >
                      Acheter maintenant
                    </div>
                  </div>
                  <div className="flex items-center flex-wrap lg:gap-20 gap-8 gap-y-4 mt-5">
                    <div
                      className="compare flex items-center gap-3 cursor-pointer"
                      onClick={handleAddToCompare}
                    >
                      <div className="compare-btn md:w-12 md:h-12 w-10 h-10 flex items-center justify-center border border-line cursor-pointer rounded-xl duration-300 hover:bg-black hover:text-white">
                        <Icon.ArrowsCounterClockwise className="heading6" />
                      </div>
                      <span>Comparer</span>
                    </div>
                  </div>
                  <div className="more-infor mt-6">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Icon.ArrowClockwise className="body1" />
                        <div className="text-title">Livraison & Retour</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon.Question className="body1" />
                        <div className="text-title">Poser une Question</div>
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-1 mt-3">
                      <Icon.Timer className="body1" />
                      <span className="text-title">Livraison estimée :</span>
                      <span className="text-secondary">
                        {formattedStartDate} - {formattedEndDate}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-3">
                      <div className="text-title">Catégories :</div>
                      <div className="text-secondary">
                        {selectedProduct?.category}, {selectedProduct?.gender}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </>
  );
};

export default ModalQuickview;
